import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, TrendingUp, Users, Zap } from "lucide-react";

const TYPES = [
  { k: "hotel", l: "Hotel" },
  { k: "travel_agency", l: "Travel Agency" },
  { k: "tour_operator", l: "Tour Operator" },
  { k: "corporate", l: "Corporate" },
  { k: "other", l: "Other" },
];

const BUCKETS = ["<10", "10-50", "50-200", "200-500", "500+"];

export default function PublicPartners() {
  const [f, setF] = useState({
    company_name: "", nif: "", contact_name: "", email: "", phone: "",
    type: "hotel", expected_monthly_bookings: "10-50", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/public/partner-application", f);
      setDone(data);
      toast.success("Application submitted");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-9 h-9 border border-[#f59e0b] flex items-center justify-center">
              <span className="font-display text-[#f59e0b] text-lg">D</span>
            </div>
            <span className="font-display text-xl text-[#0a0f1e]">DiTours</span>
          </a>
          <a href="/login" className="text-xs uppercase tracking-widest text-slate-500 hover:text-[#f59e0b]">Partner login →</a>
        </div>
      </header>

      {/* Hero */}
      <section className="py-24 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-4">Partnership programme · Lisboa</p>
          <h1 className="font-display text-6xl text-[#0a0f1e] leading-tight tracking-tight mb-6">
            Become a <span className="text-[#f59e0b]">DiTours</span> partner.
          </h1>
          <p className="text-slate-600 text-lg max-w-2xl leading-relaxed">
            Hotels, travel agencies, tour operators and corporate accounts — turn every client transfer into recurring revenue with Portugal's premium luxury mobility platform.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 border-b border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          <Benefit icon={TrendingUp} title="Recurring commission" text="Earn a margin on every booking your clients make — configurable per vehicle type, in EUR or %." />
          <Benefit icon={Zap} title="Instant integration" text="Get a dedicated booking link and iframe embed for your concierge desk in under 24 hours." />
          <Benefit icon={Users} title="Premium fleet" text="Mercedes EQB, Vito, MG Marvel L — all licensed, insured and driven by rated professionals." />
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-3">How commission works</p>
          <h2 className="font-display text-4xl text-[#0a0f1e] mb-8">Simple, transparent, monthly.</h2>
          <ol className="space-y-6 text-slate-700">
            <Step n="1">DiTours quotes each trip at its standard base price (from published fixed routes or km/hour).</Step>
            <Step n="2">You configure a markup per vehicle type — say, +€15 on Executive transfers.</Step>
            <Step n="3">Your clients see BASE + MARKUP and book through your dedicated link.</Step>
            <Step n="4">We handle payment, driver, invoicing. You get the markup as commission.</Step>
            <Step n="5">Monthly statement + PDF invoice, delivered to your accounting.</Step>
          </ol>
        </div>
      </section>

      {/* Application form */}
      <section className="py-20" id="apply">
        <div className="max-w-2xl mx-auto px-6">
          <p className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold mb-3">Apply</p>
          <h2 className="font-display text-4xl text-[#0a0f1e] mb-3">Get in touch</h2>
          <p className="text-slate-600 mb-8">We'll reply within 24 hours with next steps.</p>

          {done ? (
            <div className="bg-emerald-50 border border-emerald-200 p-8 text-center" data-testid="application-done">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
              <h3 className="font-display text-2xl text-[#0a0f1e] mb-2">Application received</h3>
              <p className="text-slate-600 mb-4">Your reference: <span className="font-mono font-bold text-[#f59e0b]">{done.reference}</span></p>
              <p className="text-sm text-slate-500">We'll contact you within 24h. Meanwhile, feel free to explore our operations at DiTours.pt.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-slate-200 p-8 shadow-sm space-y-5" data-testid="partner-application-form">
              <Grid>
                <Field l="Company name" k="company_name" f={f} setF={setF} required />
                <Field l="NIF" k="nif" f={f} setF={setF} required />
                <Field l="Contact person" k="contact_name" f={f} setF={setF} required />
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-2">Business type</label>
                  <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="input-dt" data-testid="app-type">
                    {TYPES.map((t) => <option key={t.k} value={t.k}>{t.l}</option>)}
                  </select>
                </div>
                <Field l="Email" k="email" type="email" f={f} setF={setF} required />
                <Field l="Phone" k="phone" f={f} setF={setF} required />
              </Grid>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-2">Expected monthly bookings</label>
                <select value={f.expected_monthly_bookings} onChange={(e) => setF({ ...f, expected_monthly_bookings: e.target.value })} className="input-dt" data-testid="app-volume">
                  {BUCKETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-2">Tell us about your business (optional)</label>
                <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} className="input-dt" />
              </div>
              <button type="submit" disabled={loading} data-testid="app-submit"
                className="w-full h-12 bg-[#f59e0b] text-white font-semibold text-xs uppercase tracking-widest hover:bg-[#d97706] disabled:opacity-40 shadow-sm">
                {loading ? "Sending…" : "Submit application"}
              </button>
              <p className="text-xs text-slate-500 text-center">By submitting, you agree to be contacted by DiTours regarding this partnership.</p>
            </form>
          )}
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-6xl mx-auto px-6 text-xs text-slate-500 flex justify-between">
          <div>© 2026 DiTours Lda. — Lisboa · Portugal</div>
          <div className="flex gap-6">
            <a href="/termos" className="hover:text-[#f59e0b]">Terms</a>
            <a href="/privacidade" className="hover:text-[#f59e0b]">Privacy</a>
            <a href="/cookies" className="hover:text-[#f59e0b]">Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Grid({ children }) { return <div className="grid grid-cols-2 gap-4">{children}</div>; }
function Field({ l, k, f, setF, type = "text", required }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold block mb-2">{l}</label>
      <Input type={type} required={required} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="input-dt" data-testid={`app-${k}`} />
    </div>
  );
}
function Benefit({ icon: Icon, title, text }) {
  return (
    <div className="bg-white border border-slate-200 p-6 shadow-sm">
      <div className="w-11 h-11 border border-[#f59e0b] flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-[#f59e0b]" /></div>
      <h3 className="font-display text-xl text-[#0a0f1e] mb-2">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
    </div>
  );
}
function Step({ n, children }) {
  return (
    <li className="flex gap-5">
      <div className="flex-shrink-0 w-10 h-10 border border-[#f59e0b] flex items-center justify-center font-display text-lg text-[#f59e0b]">{n}</div>
      <p className="pt-2 leading-relaxed">{children}</p>
    </li>
  );
}
